import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth, useNotifications } from "../App";
import { Bell, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

const NotificationsPage = () => {
  const { token } = useAuth();
  const { fetchUnreadCount } = useNotifications();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.get(`${API}/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotifications(response.data);
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, [token]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      fetchUnreadCount();
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      fetchUnreadCount();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "appointment": return "📅";
      case "order": return "📦";
      case "quote": return "📋";
      case "assistance": return "🔧";
      default: return "🔔";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "appointment": return "bg-blue-500/20 text-blue-400";
      case "order": return "bg-emerald-500/20 text-emerald-400";
      case "quote": return "bg-purple-500/20 text-purple-400";
      case "assistance": return "bg-orange-500/20 text-orange-400";
      default: return "bg-white/10 text-white";
    }
  };

  return (
    <div className="min-h-screen bg-black" data-testid="notifications-page">
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <h1 className="text-xl font-light text-white tracking-wide">Notifiche</h1>
        {notifications.some(n => !n.is_read) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={markAllAsRead}
            className="text-emerald-500 hover:text-emerald-400"
            data-testid="mark-all-read"
          >
            <Check size={16} className="mr-1" />
            Segna tutte
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <div className="divide-y divide-white/10">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="p-4 flex gap-4">
              <Skeleton className="w-10 h-10 rounded-full bg-zinc-800" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4 bg-zinc-800" />
                <Skeleton className="h-3 w-1/2 bg-zinc-800" />
              </div>
            </div>
          ))
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-white/60">
            <Bell size={64} className="mb-4 opacity-30" />
            <p className="text-lg">Nessuna notifica</p>
            <p className="text-sm">Le tue notifiche appariranno qui</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => !notification.is_read && markAsRead(notification.id)}
              className={`p-4 flex gap-4 transition-colors cursor-pointer ${
                notification.is_read ? "opacity-60" : "bg-white/5"
              }`}
              data-testid={`notification-${notification.id}`}
            >
              {/* Icon */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${getTypeColor(notification.type)}`}>
                {getTypeIcon(notification.type)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-white text-sm">{notification.title}</p>
                  {!notification.is_read && (
                    <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5"></span>
                  )}
                </div>
                <p className="text-sm text-white/60 mt-1">{notification.message}</p>
                <p className="text-xs text-white/40 mt-2">
                  {new Date(notification.created_at).toLocaleDateString("it-IT", {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit"
                  })}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
